<template>
  <div class="home-banner-web">
    <div ref="viewportRef" class="home-banner-web__viewport">
      <div
        ref="trackRef"
        class="home-banner-web__track"
        :class="{ 'home-banner-web__track--animating': slideCount > 1 && transitionEnabled }"
        :style="trackStyle"
      >
        <div
          v-for="(slide, trackIndex) in trackSlides"
          :key="slide.key"
          class="home-banner-web__slide"
          :style="slideStyle"
        >
          <HomeBannerFrame
            :slot-map="slide.slotMap"
            layout-mode="web"
            :clickable="slide.slideIndex === displaySlideIndex && !slide.isClone"
            :show-dots="false"
            :load-images="shouldLoadSlideImages(trackIndex)"
            :is-active-frame="slide.slideIndex === displaySlideIndex && !slide.isClone"
            @comic-click="(comic) => emit('comic-click', comic)"
          />
        </div>
      </div>
    </div>

    <div
      v-if="showDots"
      class="home-banner-web__dots-layer"
    >
      <div
        class="home-banner-web__dots"
        role="tablist"
        aria-label="轮播分页"
      >
        <button
          v-for="(_, index) in slideCount"
          :key="index"
          type="button"
          class="home-banner-web__dot"
          :class="{ 'home-banner-web__dot--active': index === displaySlideIndex }"
          :aria-label="`第 ${index + 1} 屏`"
          :aria-selected="index === displaySlideIndex"
          @pointerup.stop="onDotActivate(index, $event)"
          @keydown.enter.prevent="onDotActivate(index)"
          @keydown.space.prevent="onDotActivate(index)"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import HomeBannerFrame from '@/features/home/components/HomeBannerFrame.vue'
import { useBannerSlideTrack } from '@/features/home/composables/useBannerSlideTrack'
import { BANNER_ACTIVE_WIDTH_RATIO, buildBannerTrackSlides } from '@/features/home/utils/bannerLink'

/** @typedef {Record<number, import('@/features/home/api/homeApi').HomeBannerComic|null>} BannerSlotMap */

/**
 * @typedef {Object} TrackSlide
 * @property {BannerSlotMap} slotMap
 * @property {number} slideIndex
 * @property {string} key
 * @property {boolean} isClone
 */

const props = defineProps({
  slotMaps: {
    type: Array,
    required: true,
  },
  activeIndex: {
    type: Number,
    default: 0,
  },
  /** forward=下一屏，backward=上一屏，auto=分页点取最短路径 */
  slideDirection: {
    type: String,
    default: 'auto',
  },
  slideCount: {
    type: Number,
    default: 0,
  },
  showDots: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['comic-click', 'go-to-slide'])

const viewportRef = ref(/** @type {HTMLElement|null} */ (null))
const trackRef = ref(/** @type {HTMLElement|null} */ (null))
const viewportWidth = ref(0)
const displayTrackIndex = ref(0)
const transitionEnabled = ref(true)

/** @type {ResizeObserver|null} */
let resizeObserver = null

const trackSlides = computed(() =>
  buildBannerTrackSlides(/** @type {BannerSlotMap[]} */ (props.slotMaps))
)

const {
  displaySlideIndex,
  animateToSlide,
  animateForward,
  animateBackward,
  syncAtRest,
  isAnimating,
  cleanup,
} = useBannerSlideTrack({
  trackRef,
  transitionEnabled,
  displayTrackIndex,
  getSlideCount: () => props.slideCount,
})

function updateViewportWidth() {
  viewportWidth.value = viewportRef.value?.clientWidth ?? 0
}

function bindResizeObserver() {
  unbindResizeObserver()
  updateViewportWidth()
  if (!viewportRef.value || typeof ResizeObserver === 'undefined') return
  resizeObserver = new ResizeObserver(updateViewportWidth)
  resizeObserver.observe(viewportRef.value)
}

function unbindResizeObserver() {
  resizeObserver?.disconnect()
  resizeObserver = null
}

onMounted(async () => {
  syncAtRest(props.activeIndex)
  await nextTick()
  bindResizeObserver()
})

onUnmounted(() => {
  unbindResizeObserver()
  cleanup()
})

watch(viewportRef, (el) => {
  if (!el) return
  bindResizeObserver()
})

watch(
  () => props.activeIndex,
  (curr, prev) => {
    if (prev === undefined) {
      syncAtRest(curr)
      return
    }
    if (curr === prev) return

    if (props.slideDirection === 'forward') {
      animateForward(prev, curr)
      return
    }
    if (props.slideDirection === 'backward') {
      animateBackward(prev, curr)
      return
    }
    animateToSlide(prev, curr)
  }
)

watch(
  () => props.slideCount,
  () => {
    if (!isAnimating()) syncAtRest(props.activeIndex)
  }
)

const slideWidth = computed(() =>
  viewportWidth.value > 0 ? viewportWidth.value * BANNER_ACTIVE_WIDTH_RATIO : 0
)

const slideStyle = computed(() => {
  if (slideWidth.value <= 0) return {}
  return {
    width: `${slideWidth.value}px`,
    flex: `0 0 ${slideWidth.value}px`,
  }
})

const trackStyle = computed(() => {
  if (viewportWidth.value <= 0 || slideWidth.value <= 0) return {}
  const centerOffset = (viewportWidth.value - slideWidth.value) / 2
  const x = centerOffset - displayTrackIndex.value * slideWidth.value
  return {
    transform: `translate3d(${x}px, 0, 0)`,
  }
})

/**
 * 仅加载当前屏及相邻屏图片（含 peek 克隆，保障循环动画）
 * @param {number} trackIndex
 * @returns {boolean}
 */
function shouldLoadSlideImages(trackIndex) {
  if (props.slideCount <= 1) return true
  const slide = trackSlides.value[trackIndex]
  // 真实屏常驻 DOM，避免 loop snap 时卸载再重建导致闪黑
  if (slide && !slide.isClone) return true
  // 循环动画终点克隆屏常驻，避免末屏→首屏动画时 lazy 解码滞后
  if (
    slide?.isClone &&
    (slide.key === 'clone-next' || slide.key === 'clone-prev')
  ) {
    return true
  }
  return Math.abs(trackIndex - displayTrackIndex.value) <= 2
}

let lastDotActivateIndex = -1
let lastDotActivateAt = 0

/**
 * @param {number} index
 * @param {PointerEvent} [event]
 */
function onDotActivate(index, event) {
  if (event?.pointerType === 'mouse' && event.button !== 0) return

  const now = Date.now()
  if (index === lastDotActivateIndex && now - lastDotActivateAt < 400) return
  lastDotActivateIndex = index
  lastDotActivateAt = now

  emit('go-to-slide', index)
}
</script>

<style scoped src="@/features/home/styles/HomeBannerWebTrack.css"></style>
